import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { productService } from '../services/productService';

/**
 * Hook do obslugi skanera kodow kreskowych
 * Przechwytuje szybkie wpisywanie cyfr (typowe dla skanera) i wyszukuje produkty
 */
const useBarcodeScanner = ({ locationId, onProductFound, enabled = true }) => {
  const [searching, setSearching] = useState(false);
  const [scannerEnabled, setScannerEnabled] = useState(enabled);

  // Synchronizuj stan z propem enabled
  useEffect(() => {
    setScannerEnabled(enabled);
  }, [enabled]);

  const barcodeBufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);

  // Obsluga zeskanowanego kodu
  const handleBarcodeScanned = useCallback(async (barcode) => {
    if (searching) return;

    setSearching(true);

    try {
      const response = await productService.getInventoryProducts({
        query: barcode,
        locationId: locationId,
        limit: 1,
        availableOnly: false
      });

      if (response.success && response.products && response.products.length > 0) {
        const product = response.products[0];

        if (onProductFound) {
          onProductFound(product);
        }
      } else {
        toast.error(`Nie znaleziono produktu o kodzie: ${barcode}`);
      }
    } catch (err) {
      console.error('Blad wyszukiwania produktu:', err);
      toast.error(`Blad wyszukiwania: ${err.message}`);
    } finally {
      setSearching(false);
    }
  }, [searching, locationId, onProductFound]);

  // Globalny listener klawiszy
  useEffect(() => {
    if (!scannerEnabled) return;

    const SCANNER_SPEED = 60; // Skaner wpisuje <60ms miedzy znakami
    const MIN_LENGTH = 8;

    const handleGlobalKeyDown = (e) => {
      const now = Date.now();
      const timeDiff = now - lastKeyTimeRef.current;

      // Reset jesli za dluga przerwa (>200ms)
      if (timeDiff > 200 && barcodeBufferRef.current.length > 0) {
        barcodeBufferRef.current = '';
      }

      // Cyfra - ZAWSZE blokuj jesli to wyglada na skaner
      if (/^\d$/.test(e.key)) {
        const isQuickSequence = timeDiff < SCANNER_SPEED;
        const hasBuffer = barcodeBufferRef.current.length > 0;

        // Jesli juz mamy cyfry w buforze i to szybka sekwencja - to skaner
        // Lub jesli to pierwsza cyfra ale poprzednia tez byla szybka
        if (hasBuffer && isQuickSequence) {
          // To skaner - blokuj i buforuj
          e.preventDefault();
          e.stopPropagation();
          barcodeBufferRef.current += e.key;
        } else if (!hasBuffer || !isQuickSequence) {
          // Pierwsza cyfra lub wolne wpisywanie - pozwol przejsc ale buforuj
          barcodeBufferRef.current += e.key;
        }

        lastKeyTimeRef.current = now;
        return;
      }

      // Enter - zakoncz skanowanie
      if (e.key === 'Enter' && barcodeBufferRef.current.length >= MIN_LENGTH) {
        const barcode = barcodeBufferRef.current;
        barcodeBufferRef.current = '';

        if (/^\d+$/.test(barcode)) {
          e.preventDefault();
          e.stopPropagation();

          // Wyczysc input - uzyj React-friendly sposobu
          const active = document.activeElement;
          if (active && active.tagName === 'INPUT' && active.type === 'text') {
            // Ustaw pusta wartosc i wywolaj zdarzenia React
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
            nativeInputValueSetter.call(active, '');
            active.dispatchEvent(new Event('input', { bubbles: true }));
          }

          handleBarcodeScanned(barcode);
        }
        return;
      }

      lastKeyTimeRef.current = now;

      // Inny klawisz - reset bufora
      if (!/^\d$/.test(e.key) && e.key !== 'Shift') {
        barcodeBufferRef.current = '';
      }
    };

    // Capture na document - lapie PRZED innymi handlerami
    document.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [scannerEnabled, handleBarcodeScanned]);

  return {
    searching,
    scannerEnabled,
    setScannerEnabled,
  };
};

export default useBarcodeScanner;
